import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isAuthenticated, token } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch {
      // Cart fetch may fail when unauthenticated — silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0 });
    }
  }, [isAuthenticated, token, fetchCart]);

  const addToCart = async (artworkId) => {
    try {
      setLoading(true);
      await axios.post(`${API}/cart/add`, { artwork_id: artworkId });
      await fetchCart();
      toast.success('Added to cart');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add to cart';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (artworkId) => {
    try {
      setLoading(true);
      await axios.delete(`${API}/cart/${artworkId}`);
      await fetchCart();
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove from cart');
    } finally {
      setLoading(false);
    }
  };

  const checkout = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Checkout failed';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    setCart({ items: [], total: 0 });
  };

  const value = {
    cart,
    loading,
    addToCart,
    removeFromCart,
    checkout,
    clearCart,
    fetchCart,
    itemCount: cart.items.length,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
